import { useEffect, useState } from "react"

import { type FeedbackAttachmentKind, getAdminFeedbackAttachment } from "@/api/feedback-api"

type AttachmentState = {
  key: string | null
  url: string | null
  status: "idle" | "loading" | "ready" | "error"
}

const INITIAL_STATE: AttachmentState = { key: null, url: null, status: "idle" }

/** Load an admin feedback attachment through the authenticated API client. */
export function useAdminFeedbackAttachment(
  logId: string | null,
  kind: FeedbackAttachmentKind,
  enabled: boolean
) {
  const key = enabled && logId ? `${logId}:${kind}` : null
  const [state, setState] = useState<AttachmentState>(INITIAL_STATE)

  useEffect(() => {
    if (!key || !logId) {
      setState(INITIAL_STATE)
      return
    }

    const abortController = new AbortController()
    let objectUrl: string | null = null
    let cancelled = false
    setState({ key, url: null, status: "loading" })

    void getAdminFeedbackAttachment(logId, kind, abortController.signal)
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setState({ key, url: objectUrl, status: "ready" })
      })
      .catch(() => {
        if (!cancelled) setState({ key, url: null, status: "error" })
      })

    return () => {
      cancelled = true
      abortController.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [key, kind, logId])

  if (!key || state.key !== key) {
    return { url: null, isLoading: Boolean(key), hasError: false }
  }
  return {
    url: state.url,
    isLoading: state.status === "loading",
    hasError: state.status === "error",
  }
}
